import { useState } from 'react';
import {
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Checkbox,
  Label,
  Input,
} from '@rnd-payplus/alloy-react';
import { Plus, X } from 'lucide-react';

interface FilterButtonProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
}

export function FilterButton({ label, options, selected, onChange }: FilterButtonProps) {
  const [open, setOpen] = useState(false);
  const hasSelection = selected.length > 0;

  const toggle = (value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`h-9 justify-between gap-2 shadow-sm ${
            hasSelection ? 'border-primary bg-accent text-accent-foreground' : ''
          }`}
        >
          <span className="flex items-center gap-2">
            {!hasSelection && <Plus className="h-4 w-4" />}
            {hasSelection ? `${label}: ${selected.join(', ')}` : label}
          </span>
          {hasSelection && (
            <span role="button" onClick={clear} className="ml-1">
              <X className="h-4 w-4" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <div className="max-h-[300px] overflow-y-auto space-y-1">
          {options.map((option) => (
            <label
              key={option}
              className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer hover:bg-accent"
            >
              <Checkbox
                checked={selected.includes(option)}
                onCheckedChange={() => toggle(option)}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface SearchFilterButtonProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

export function SearchFilterButton({ label, value, onChange }: SearchFilterButtonProps) {
  const [open, setOpen] = useState(false);
  const hasValue = value.length > 0;

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`h-9 justify-between gap-2 shadow-sm ${
            hasValue ? 'border-primary bg-accent text-accent-foreground' : ''
          }`}
        >
          <span className="flex items-center gap-2">
            {!hasValue && <Plus className="h-4 w-4" />}
            {hasValue ? `${label}: ${value}` : label}
          </span>
          {hasValue && (
            <span role="button" onClick={clear} className="ml-1">
              <X className="h-4 w-4" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <Input
          placeholder={`Filter by ${label.toLowerCase()}...`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
}
